import { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PageHeader from '../../components/ui/PageHeader';

function parseSseChunk(buffer, onEvent) {
  const parts = buffer.split('\n\n');
  const rest = parts.pop() || '';
  for (const part of parts) {
    const event = part.match(/^event:\s*(.+)$/m)?.[1];
    const dataRaw = part.match(/^data:\s*(.+)$/m)?.[1];
    if (!event || !dataRaw) continue;
    try {
      onEvent(event, JSON.parse(dataRaw));
    } catch {
      onEvent(event, { raw: dataRaw });
    }
  }
  return rest;
}

export default function AiV2() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [currentNode, setCurrentNode] = useState('');
  const [cost, setCost] = useState(null);
  const [evidence, setEvidence] = useState([]);
  const [artifacts, setArtifacts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const abortRef = useRef(null);
  const artifactHref = (id) => {
    const token = localStorage.getItem('token');
    return `/api/ai-v2/artifacts/${id}${token ? `?t=${encodeURIComponent(token)}` : ''}`;
  };

  const canSend = useMemo(() => input.trim() && !loading, [input, loading]);

  const handleEvent = (event, data) => {
    setEvents((prev) => [{ event, data, ts: new Date().toLocaleTimeString() }, ...prev].slice(0, 20));
    if (event === 'session') setSessionId(data.sessionId);
    if (event === 'node') setCurrentNode(`${data.node}: ${data.status}`);
    if (event === 'tool_start') setCurrentNode(`tool: ${data.title}`);
    if (event === 'warning') setError(data.message || 'Предупреждение');
    if (event === 'final') {
      setCost(data.cost || null);
      setEvidence(data.evidence || []);
      setArtifacts(data.artifacts || []);
      setMessages((prev) => [...prev, { role: 'assistant', content: data.answer, evidence: data.evidence || [] }]);
    }
    if (event === 'error') setError(data.error || 'Ошибка AI Agent v2');
  };

  const send = async () => {
    const message = input.trim();
    if (!message) return;
    setLoading(true);
    setError('');
    setInput('');
    setEvidence([]);
    setArtifacts([]);
    setMessages((prev) => [...prev, { role: 'user', content: message }]);

    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const token = localStorage.getItem('token');
      const res = await fetch('/api/ai-v2/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message, sessionId }),
        signal: controller.signal,
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        buffer = parseSseChunk(buffer, handleEvent);
      }
    } catch (err) {
      if (err.name !== 'AbortError') setError(err.message || 'Ошибка запроса');
    } finally {
      setLoading(false);
      setCurrentNode('');
      abortRef.current = null;
    }
  };

  return (
    <Box>
      <PageHeader
        title="AI Agent v2"
        subtitle="Новый независимый LangGraph-чат: read-only доступ к БД, API, коду, docs и artifacts"
        actions={cost && (
          <Chip
            color={cost.warning ? 'warning' : 'primary'}
            label={`$${Number(cost.cost_usd || 0).toFixed(4)} · ${cost.total_tokens || 0} токенов · ${cost.tool_calls || 0} tools`}
          />
        )}
      />

      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Stack direction={{ xs: 'column', lg: 'row' }} spacing={2}>
        <Paper sx={{ flex: 1, p: 2, minHeight: 560 }}>
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {messages.length === 0 && (
                <Card variant="outlined">
                  <CardContent>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SmartToyIcon color="primary" />
                      <Typography fontWeight={700}>Готов к вопросу</Typography>
                    </Stack>
                    <Typography color="text.secondary" sx={{ mt: 1 }}>
                      Можно спрашивать про БД, код, локальные документы или read-only API маркетплейсов. Большие результаты сохраняются как artifacts.
                    </Typography>
                  </CardContent>
                </Card>
              )}
              <Stack spacing={1.5}>
                {messages.map((msg, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                      maxWidth: '85%',
                      bgcolor: msg.role === 'user' ? 'primary.light' : 'surfaceContainerLow',
                      color: msg.role === 'user' ? 'primary.dark' : 'text.primary',
                      borderRadius: 3,
                      p: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <Typography variant="body2">{msg.content}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>

            {currentNode && (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">{currentNode}</Typography>
              </Stack>
            )}

            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                value={input}
                placeholder="Например: найди, где считается profit WB, или покажи схему analytics"
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) send();
                }}
              />
              <Button
                variant="contained"
                endIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                disabled={!canSend}
                onClick={send}
                sx={{ alignSelf: 'stretch', borderRadius: 3 }}
              >
                Отправить
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack spacing={2} sx={{ width: { xs: '100%', lg: 390 } }}>
          <Paper sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>Evidence</Typography>
            <Stack spacing={1}>
              {evidence.length === 0 && <Typography color="text.secondary" variant="body2">Источники появятся после ответа.</Typography>}
              {evidence.map((item, idx) => (
                <Box key={idx}>
                  <Typography variant="body2" fontWeight={600}>{item.title || item.source}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {item.summary}
                  </Typography>
                  {item.artifactId && <Chip size="small" label={`artifact ${item.artifactId.slice(0, 8)}`} sx={{ mt: 0.5 }} />}
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>Artifacts</Typography>
            <Stack spacing={1}>
              {artifacts.length === 0 && <Typography color="text.secondary" variant="body2">Большие результаты будут сохранены здесь.</Typography>}
              {artifacts.map((artifact) => (
                <Box key={artifact.id}>
                  <Link href={artifactHref(artifact.id)} target="_blank" rel="noreferrer">
                    {artifact.title || artifact.id}
                  </Link>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {artifact.bytes} байт · {artifact.source}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ p: 2 }}>
            <Typography fontWeight={700} sx={{ mb: 1 }}>События SSE</Typography>
            <Stack spacing={0.5}>
              {events.map((evt, idx) => (
                <Typography key={idx} variant="caption" color="text.secondary">
                  {evt.ts} · {evt.event}
                </Typography>
              ))}
            </Stack>
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
}
